// @flow
import React from 'react';
import { get, isEmpty } from 'lodash';
import { SettingsMenu, PlatformSwitch, AppMobileHeader, AppMobileFooter, BlockedAccountNotice } from '@blocks';
import { Loading } from '@elements';
import type { User, SubscriptionState, BundledPermissions, WebBillingDestination } from '@types';
import { withTranslation } from 'react-i18next';
import { createNsTranslator, utcFormat, isPresent, nl2br, isIOSApp } from '@helpers/helpers';
import PlanSummary, { PlanHelp, planState } from './Partials/PlanSummary';
import AppStoreSwitch from './Partials/AppStoreSwitch';
import { StoreReviewSubscription } from '../StoreReview/StoreReviewPlan';
import './SubscriptionInfo.scss';

type Props = {
  user: User,
  subscription: SubscriptionState,
  onLogout: Function,
  t: Function,
  permissions: BundledPermissions,
  onGetNewBoleto: Function,
  onManageBilling: Function,
  onUpdateCard: Function,
  onResubscribeClick: Function,
  isRequestingNewBoleto: boolean,
  // The billing page whose link is being created (its button spins, the others wait)
  openingBillingPage: ?WebBillingDestination,
  // An iOS build in App Store review: the plan's name, nothing about paying for it
  storeReview?: boolean,
};

class SubscriptionInfo extends React.Component<Props> {
  render() {
    const {
      user, subscription, onLogout, onGetNewBoleto, isRequestingNewBoleto, onManageBilling, onUpdateCard, onResubscribeClick,
      openingBillingPage, storeReview,
    } = this.props;

    const _ = createNsTranslator('my_plan', this.props.t);
    // The app's gate (AuthenticatedRoute) closes everything but a few pages to a blocked account
    const blocked = get(user, ['advancedData', 'blocked'], false);
    const blockedStatus = blocked ? get(user, ['advancedData', 'subscriptionStatus'], null) : null;

    const isLoading = get(subscription, 'isLoading', false);
    const subscriptionData = get(subscription, 'subscriptionData');
    const data = (key: string | Array<string>, fallback: any = null) => get(subscriptionData, key, fallback);

    // Only what the page shows: the rest is on the web billing pages
    const sbInfo = {
      paymentPeriod: data('paymentPeriod', 'month'),
      dealSummary: isPresent(data('dealSummary')) ? nl2br(data('dealSummary')) : null,
      price: data('subscriptionPrice'),
      paymentMethod: data('paymentMethod', false),
      planName: data('planName', ''),
      nextPaymentDate: utcFormat(data('nextPaymentDate', ''), 'DD/MM/YYYY', ' '),
      warning: data('warning'),
      subscriptionStatus: data('subscriptionStatus'),
      boleto: {
        dueDate: utcFormat(data(['boleto', 'dueDate']), 'DD/MM/YYYY'),
        price: data(['boleto', 'price']),
        url: data(['boleto', 'url']),
      },
      invoicePaymentLink: data('invoicePaymentLink'),
      blocked,
      blockedStatus,
    };

    const showAppStoreSwitch = planState(sbInfo) === 'app_store' && !isIOSApp();
    const renderContent = () => (storeReview ? (
      <>
        <BlockedAccountNotice place="plan" />
        <StoreReviewSubscription planName={sbInfo.planName} paymentMethod={sbInfo.paymentMethod} warning={sbInfo.warning} blocked={blocked} _={_} />
      </>
    ) : (
      <>
        <BlockedAccountNotice place="plan" />
        {isEmpty(subscriptionData) ? (
          <section className="planSummary"><p className="planSummary-error" role="alert">{_('summary/load_error')}</p></section>
        ) : (
          <PlanSummary
            sbInfo={sbInfo}
            onManageBilling={onManageBilling}
            onUpdateCard={onUpdateCard}
            onGetNewBoleto={onGetNewBoleto}
            onResubscribeClick={onResubscribeClick}
            isRequestingNewBoleto={isRequestingNewBoleto}
            openingBillingPage={openingBillingPage}
            _={_}
          />
        )}
        {/* The switch card ends in its own support link */}
        {showAppStoreSwitch ? <AppStoreSwitch _={_} /> : <PlanHelp _={_} />}
      </>
    ));

    return (
      <PlatformSwitch
        inject
        desktop={() => (
          <div id="myPlansPage" className="appContent settingsPage">
            <aside className="sidebar">
              <SettingsMenu user={user} onLogout={onLogout} permissions={this.props.permissions} />
            </aside>
            {isLoading && <section className="mainSection"><Loading /></section>}
            {!isLoading && (
              <section className="mainSection">
                <div className="centerContent">
                  {renderContent()}
                </div>
              </section>
            )}
          </div>
        )}
        mobile={() => (
          <React.Fragment>
            {/* A blocked account may have landed here first (the gate), with nothing to go back to: Ajustes,
                where "Sair" is, is the way out */}
            <AppMobileHeader title={_('label/title')} fallbackBackTo={blocked ? '/ajustes' : undefined} />
            <div id="myPlansPage" className="appContent settingsPage">
              {isLoading && <section className="mainSection"><Loading /></section>}
              {!isLoading && (
              <section className="mainSection">
                <div className="centerContent">
                  {renderContent()}
                </div>
              </section>
              )}
            </div>
            <AppMobileFooter />
          </React.Fragment>
        )}
      />
    );
  }
}


export default withTranslation()(SubscriptionInfo);
